import {
    EventbriteEvent,
    getAttendeeTimezone,
    getEventDatesFormat,
    getFormattedEventDate as getFormattedEventDateStandardized,
    shouldShowDateInAttendeeTimezone,
    TimezoneLocale,
} from '@eventbrite/event-date-time';
import { gettext } from '@eventbrite/i18n';
import { CalendarCheckmarkIcon } from '@eventbrite/marmalade';
import loadable from '@loadable/component';
import classNames from 'classnames';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import type {
    ChildEventProps,
    DateAndTimeScopedProps,
    Event,
    LocalizedDateTime,
} from '../../../../types';
import {
    Detail,
    DetailContent,
    DetailInner,
} from '../../../_shared/Detail/Detail';
import { LoadingSkeleton } from '../../../_shared/LoadingSkeleton/LoadingSkeleton';
import {
    EventDetailsSection,
    EventDetailsSectionTitle,
} from '../../EventDetails';
import { ChildEventsDropdown } from '../ChildEventsSelection/ChildEventsDropdown';
import { ChildEventsSlider } from '../ChildEventsSelection/ChildEventsSlider';
import './DateAndTime.scss';
import { DoorTime } from './DoorTime';
import {
    eventIsAvailable,
    findEventOnAvailableEvents,
    findNextAvailableEvent,
} from './utils';

export type DateAndTimeProps = DateAndTimeScopedProps & {
    isRepeatingChildEventsSliderExperiment: boolean;
    locale: string;
    event: Pick<
        Event,
        | 'id'
        | 'start'
        | 'end'
        | 'hideStartDate'
        | 'hideEndDate'
        | 'childEvents'
        | 'isOnlineEvent'
    > & {
        listingDisplayFlags: {
            shouldShowTimezone: boolean;
        };
    };
};

type MoreOptions = {
    onButtonClick: () => void;
};

const formattedEventDateStandardized = ({
    event,
    locale,
    isOnlineEvent,
}: {
    event: EventbriteEvent;
    locale: string;
    isOnlineEvent: boolean;
}) => {
    const getTimezoneFrom = isOnlineEvent
        ? TimezoneLocale.Attendee
        : TimezoneLocale.Creator;

    return getFormattedEventDateStandardized({
        event,
        locale,
        datesFormat: getEventDatesFormat({
            event,
            locale,
            useTimezoneFrom: getTimezoneFrom,
        }),
        useTimezoneFrom: getTimezoneFrom,
    });
};

export const DateAndTime = ({
    event,
    displayDate,
    shouldShowDoorTime,
    doorTime,
    isRepeatingChildEventsSliderExperiment,
    locale,
}: DateAndTimeProps) => {
    const { id: eventId, childEvents, isOnlineEvent } = event;
    const [moreOptionsModalOpened, setMoreOptionsModalOpened] = useState(false);
    const [isLoadingUserLocalization, setIsLoadingUserLocalization] =
        useState(isOnlineEvent);
    const [formattedDateStandardized, setFormattedDateStandardized] = useState(
        () => {
            return formattedEventDateStandardized({
                event,
                locale,
                isOnlineEvent,
            });
        },
    );

    useEffect(() => {
        if (isOnlineEvent) {
            setFormattedDateStandardized(
                formattedEventDateStandardized({
                    event,
                    locale,
                    isOnlineEvent,
                }),
            );
            setIsLoadingUserLocalization(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!displayDate) {
        return null;
    }

    const LABEL_ID = 'date-time-heading';

    const DateTimeLinesStandardized = () => {
        const dateTime = shouldShowDoorTime ? (
            <div>
                <>
                    {formattedDateStandardized}.{' '}
                    <DoorTime
                        dateAndTimezone={{
                            utc: doorTime as string,
                            timezone: event.start.timezone,
                        }}
                        locale={locale}
                        isOnlineEvent={isOnlineEvent}
                    />
                </>
            </div>
        ) : (
            formattedDateStandardized
        );

        const dateTimeClasses = classNames('date-info__full-datetime', {
            'eds-is-hidden-accessible': isLoadingUserLocalization,
        });

        return (
            <div>
                {isLoadingUserLocalization && (
                    <LoadingSkeleton width="100%" borderRadius="4px" />
                )}
                <span className={dateTimeClasses}>{dateTime}</span>
            </div>
        );
    };

    const shouldShowChildEvents = childEvents && childEvents.length > 0;

    const userTimezone = getAttendeeTimezone();
    const shouldShowChildEventsInAttendeeTimeZone =
        shouldShowDateInAttendeeTimezone({ isOnlineEvent, userTimezone });

    const getChildEventsInAttendeeTimeZone: (
        childEvents: ChildEventProps[],
    ) => Array<ChildEventProps> = () => {
        const childEventsInAttendeeTimeZone: Array<ChildEventProps> = [];

        childEvents?.forEach((childEvent) => {
            const formatedDate = moment
                .tz(childEvent.start.utc, userTimezone)
                .format('YYYY-MM-DDTHH:mm:ss');

            const childEventInAttendeeTimeZone: ChildEventProps = {
                ...childEvent,
                start: {
                    ...childEvent.start,
                    local: formatedDate,
                    timezone: userTimezone as string,
                },
            };
            childEventsInAttendeeTimeZone.push(childEventInAttendeeTimeZone);
        });

        return childEventsInAttendeeTimeZone;
    };

    const eventStartInAttendeeTimeZone = {
        local: moment
            .tz(event.start.utc, userTimezone)
            .format('YYYY-MM-DDTHH:mm:ss'),
        timezone: userTimezone,
        utc: event.start.utc,
    };

    const LazyMoreOptionsModal = loadable(
        () =>
            import('../ChildEventsSelection/MoreOptionsModal/MoreOptionsModal'),
    );

    const handleMoreOptionsClick = () => {
        setMoreOptionsModalOpened(true);
    };

    const MORE_OPTIONS = gettext('More options');

    const MoreOptionsButton = ({ onButtonClick }: MoreOptions) => (
        <div className="more-options-button">
            <button
                className="eds-btn eds-btn--link"
                data-testid="more-options-button-mobile"
                onClick={onButtonClick}
            >
                {MORE_OPTIONS}
            </button>
        </div>
    );

    const availableChildEvents = childEvents
        ? childEvents.filter(eventIsAvailable)
        : [];

    const selectedEventOnModal =
        findEventOnAvailableEvents(event, availableChildEvents) ??
        findNextAvailableEvent(event, availableChildEvents);
    return (
        <>
            <EventDetailsSection aria-labelledby={LABEL_ID}>
                <Detail>
                    <DetailInner>
                        <DetailContent>
                            <EventDetailsSectionTitle>
                                {isRepeatingChildEventsSliderExperiment ? (
                                    <h2>{gettext('Select date and time')}</h2>
                                ) : (
                                    <h2>{gettext('Date and time')}</h2>
                                )}
                            </EventDetailsSectionTitle>
                            <div
                                className={classNames({
                                    'select-date-and-time':
                                        isRepeatingChildEventsSliderExperiment,
                                })}
                            >
                                <div className="date-info">
                                    <CalendarCheckmarkIcon className="date-info__icon" />
                                    <div>
                                        <DateTimeLinesStandardized />
                                        {shouldShowChildEvents ? (
                                            isRepeatingChildEventsSliderExperiment ? (
                                                <MoreOptionsButton
                                                    onButtonClick={
                                                        handleMoreOptionsClick
                                                    }
                                                />
                                            ) : (
                                                <ChildEventsDropdown
                                                    childEvents={
                                                        childEvents as Array<ChildEventProps>
                                                    }
                                                    eventId={eventId}
                                                    isOnlineEvent={
                                                        isOnlineEvent
                                                    }
                                                />
                                            )
                                        ) : null}
                                    </div>
                                </div>
                                {shouldShowChildEvents &&
                                isRepeatingChildEventsSliderExperiment ? (
                                    <ChildEventsSlider
                                        childEvents={
                                            shouldShowChildEventsInAttendeeTimeZone
                                                ? getChildEventsInAttendeeTimeZone(
                                                      availableChildEvents,
                                                  )
                                                : availableChildEvents
                                        }
                                        eventId={eventId}
                                        currentEventStartDate={
                                            shouldShowChildEventsInAttendeeTimeZone
                                                ? (eventStartInAttendeeTimeZone as LocalizedDateTime)
                                                : selectedEventOnModal.start
                                        }
                                        currentEventId={selectedEventOnModal.id}
                                    />
                                ) : null}
                            </div>
                        </DetailContent>
                    </DetailInner>
                </Detail>
                {moreOptionsModalOpened && (
                    <LazyMoreOptionsModal
                        onClose={() => setMoreOptionsModalOpened(false)}
                        opened={moreOptionsModalOpened}
                        childEvents={childEvents}
                        currentEventStartDate={selectedEventOnModal.start}
                        currentEventId={selectedEventOnModal.id}
                    />
                )}
            </EventDetailsSection>
        </>
    );
};
